import dayjs from 'dayjs';
import { createUseStyles } from 'react-jss';
import { PrivacyBadge } from '../../components/PrivacyBadge';
import { ICaremapNote } from '../model/NotesClient';
import { CareMapState } from '../../caremap/CareMapState';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { ISupportGroupKey } from '../../caremap/ICareMap';
import { highlightMatch } from '../utils/highlightMatch';
import { ThumbtackIcon } from '../../theme/Icons';
import { useHasPrivacyAudience } from '../../support/useHasPrivacyAudience';

const useStyles = createUseStyles({
    titleWrapper: {
        fontWeight: '500 !important',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'end',
        // .sc-ion-label-md-s h3 sets a smaller font-size than we want
        fontSize: 'inherit !important'
    },

    title: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },

    timeAndTarget: {
        // .sc-ion-label-md-s h3 sets a smaller font-size than we want
        fontSize: '15px !important',
        marginTop: '8px !important',
        marginBottom: '6px !important'
    }
});

const NoteTargetHeader: React.FC<{ note: ICaremapNote }> = ({ note }) => {
    const careMapState = useInstance(CareMapState);
    const activeCaremap = careMapState.getActiveCaremap();

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    if(!activeCaremap){
        return null;
    }

    const noteTarget = note.otherAttributes.noteTarget;

    if (!noteTarget){
        return null;
    }

    if (noteTarget.section === 'patient'){
        return null;
    }

    let children: React.ReactNode;
    const middot = <span style={{ marginLeft: 3, marginRight: 3, marginBottom: 3, fontSize: '2.5em', lineHeight: 0}}>&middot;</span>;

    if (noteTarget.section === 'household'){
        if (!noteTarget.memberId) {
            children = <span>Inner Circle</span>;
        } else {
            // A pushed note can target a member not yet in the loaded caremap; render no
            // target header rather than throwing (getHouseholdMember throws on a miss).
            const member = careMapState.maybeGetHouseholdMember(noteTarget.memberId);
            if (!member) {
                return null;
            }
            children = <span className="mp-mask"><MemberName {...member} /></span>;
        }
    }
    else if (!noteTarget.memberId){
        const sectionTitle = (careMapState.getAllSupportGroupLabels() as any)[noteTarget.section]?.title || noteTarget.section;
        children = <span>{sectionTitle}</span>;
    }
    else {
        const member = careMapState.getSupportGroupMember({ supportGroupKey: noteTarget.section as ISupportGroupKey, memberId: noteTarget.memberId });
        if (!member || (!member.role && !member.name)){
            return null;
        }
        children = (
            <span>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    {member.role && (
                        <>
                            {member.role}
                            {middot}
                        </>
                    )}
                    {member.name}
                </div>
            </span>
        );
    }

    return (
        <span>
            <div style={{display: 'flex', alignItems: 'center'}}>
                {middot}
                {children}
            </div>
        </span>
    );
};

const MemberName: React.FC<{ firstName?: string; lastName?: string; }> = ({ firstName, lastName }) => {
    let name = '';
    if (firstName){
        name = firstName;
    }
    if (name && lastName){
        name  = name + ' ';
    }
    if (lastName){
        name = name + lastName;
    }

    return name;
};

const NoteFormattedDate: React.FC<{ date?: string; useFullFormat?: boolean }> = ({ date, useFullFormat }) => {
    const formatDate = () => {
        const dayjsDate = dayjs(date);
        if (dayjsDate.isToday()) {
            return dayjsDate.format('h:mma');
        }
        const format = useFullFormat ? 'MMMM D, YYYY [at] h:mma' : 'MM/DD/YY';
        return dayjsDate.format(format);
    };

    return <span style={useFullFormat ? {} : {fontWeight: 500}}>{formatDate()}</span>;
};

export const NoteDateTarget: React.FC<{ note: ICaremapNote; showNoteTargetHeader?: boolean; showBothDates?: boolean; }> = ({ note, showNoteTargetHeader, showBothDates }) => {
    const formatDate = (date?: string) => {
        const dayjsDate = dayjs(date);
        const format = dayjsDate.isToday() ? 'h:mma' : 'MM/DD/YY';
        return dayjsDate.format(format);
    };

    const createdFormatted = formatDate(note.createdAt);
    const updatedFormatted = formatDate(note.updatedAt);
    const formattedDatesAreSame = createdFormatted === updatedFormatted;

    return (
        <div style={{display: 'flex', alignItems: 'center', color: 'var(--ion-color-step-600, #666666)', fontSize: 15}}>
            {showBothDates ? (
                <div style={{fontSize: '13px'}}>
                    {formattedDatesAreSame ? (
                        <>Created <NoteFormattedDate date={note.createdAt} useFullFormat /></>
                    ) : (
                        <>Modified <NoteFormattedDate date={note.updatedAt} useFullFormat /></>
                    )}
                </div>
            ) : (
                <NoteFormattedDate date={note.updatedAt} />
            )}
            {showNoteTargetHeader && (
                <NoteTargetHeader note={note}/>
            )}
        </div>
    );
};

export const CareMapNotesListTitle: React.FC<{
    note: ICaremapNote;
    showPrivacy: boolean;
    showNoteTargetHeader?: boolean;
    sortBy?: 'updatedAt' | 'createdAt';
    searchText?: string;
    isPinned?: boolean;
}> = ({ note, showPrivacy = false, showNoteTargetHeader = false, sortBy = 'updatedAt', searchText = '', isPinned = false }) => {
    const classes = useStyles();
    const hasPrivacyAudience = useHasPrivacyAudience();

    return (
        <>
            <h3 className={classes.titleWrapper}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    {isPinned && (
                        <span data-type="pin-indicator" style={{ flexShrink: 0 }}>
                            <ThumbtackIcon color="medium" size="small" />
                        </span>
                    )}
                    <div className={classes.title}>
                        {note.title && <span data-type="title">{highlightMatch(note.title, searchText)}</span>}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {showPrivacy && hasPrivacyAudience && note.isPrivate ? <PrivacyBadge useLock /> : null}
                </div>
            </h3>
            <h3 className={classes.timeAndTarget}>
                <div style={{display: 'flex', alignItems: 'center', color: 'var(--ion-color-step-600, #666666)', fontSize: 15}}>
                    <NoteFormattedDate date={sortBy === 'createdAt' ? note.createdAt : note.updatedAt} />
                    {showNoteTargetHeader && (
                        <NoteTargetHeader note={note}/>
                    )}
                </div>
            </h3>
        </>
    );
};
